import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Calendar, ArrowLeft, Globe, Wifi, ClipboardList, LogIn, UserPlus, CalendarPlus, CheckCircle2, XCircle, Clock, Info, Users, Building2 } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatDateRange, localize, languageLabel, professionCategoryLabel, specialtyLabel, PROFESSIONS, ENGAGEMENT_TYPES, COMPENSATION_TYPES } from "../lib/meta";
import { canonicalLang } from "../lib/taxonomy";
import { downloadInitiativeIcs } from "../lib/calendar";
import { StatusBadge } from "../components/StatusBadge";
import { ShareInitiative } from "../components/ShareInitiative";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

export default function InitiativeDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [i, setI] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ availability: "", dates: "", format_availability: "", notes: "" });
  const remindedRef = useRef(false);
  const viewedRef = useRef(false);

  const load = () => api.get(`/initiatives/${id}`).then((r) => setI(r.data)).catch(() => setI(false)).finally(() => setLoading(false));
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (user?.role === "psychologist" && user?.psychologist_id) {
      api.get("/psychologists/me").then((r) => setMyProfile(r.data)).catch(() => {});
    }
  }, [user]);

  // Track a single page view for reach analytics (published/closed only).
  useEffect(() => {
    if (!viewedRef.current && i && (i.status === "published" || i.status === "closed")) {
      viewedRef.current = true;
      api.post(`/initiatives/${id}/track`, { event: "view" }).catch(() => {});
    }
  }, [i, id]);

  const publicUrl = `${window.location.origin}/iniciativas/${id}`;
  const goal = i?.participation_goal || 0;
  const myStatus = i?.my_status || null;
  const isFull = !!i?.is_full;
  const hasValidDates = !!i?.start_date;
  const remaining = i?.remaining ?? 0;
  const isOpp = !!i?.is_opportunity;
  const deadlinePassed = isOpp && !!i?.deadline_passed;
  const groupMismatch = isOpp && !!i?.required_profession_group && !!myProfile && (myProfile.profession_group || "") !== i.required_profession_group;
  const professionMismatch = (isOpp && !!i?.required_profession && !!myProfile && myProfile.profession !== i.required_profession) || groupMismatch;
  const hasStructuredReq = !!(i?.required_profession || i?.required_profession_group || i?.required_specialty || i?.required_languages?.length || i?.engagement_type || i?.min_experience != null || i?.required_credential_status === "verified");
  const applyLabel = isOpp ? t("opportunities.apply") : t("initiatives.participate");

  // Return-to-initiative reminder (once per arrival, only if not yet requested).
  useEffect(() => {
    if (!i || remindedRef.current) return;
    const intended = localStorage.getItem("auna_intended_initiative");
    const eligible = user?.role === "psychologist" && user?.psychologist_id && i.status === "published" && !myStatus && !isFull && !professionMismatch;
    if (intended === id && eligible) {
      remindedRef.current = true;
      toast(t("initiatives.reminder_text"), {
        action: { label: t("initiatives.participate"), onClick: () => setOpen(true) },
      });
    }
  }, [i, user, id, myStatus, isFull, t]);

  if (loading) return <div className="flex min-h-[50vh] items-center justify-center text-muted-foreground">{t("common.loading")}</div>;
  if (!i) return <div className="mx-auto max-w-3xl px-4 py-24 text-center text-muted-foreground">404</div>;

  const pct = goal > 0 ? Math.min(100, Math.round((i.confirmed / goal) * 100)) : 0;
  const datesDisplay = formatDateRange(i.start_date, i.end_date, lang) || i.dates;
  const modalityLabel = (i.online_required && i.in_person_required)
    ? t("initiatives.online_and_in_person")
    : i.online_required ? t("common.online") : i.in_person_required ? t("common.in_person") : null;
  const shareable = i.status === "published" || i.status === "closed";
  const showReminderBanner = localStorage.getItem("auna_intended_initiative") === id
    && user?.role === "psychologist" && user?.psychologist_id && i.status === "published" && !myStatus && !isFull && !professionMismatch;
  const spotsText = (n) => (n === 1 ? t("initiatives.spots_remaining_one") : t("initiatives.spots_remaining_other")).replace("{n}", n);
  const joinedText = (n) => `${n} ${n === 1 ? t("initiatives.joined_singular") : t("initiatives.joined_plural")}`;

  const rememberAndGo = (path) => { localStorage.setItem("auna_intended_initiative", id); navigate(path); };
  const handleParticipateClick = () => {
    if (!user) { rememberAndGo("/login"); return; }
    if (user.role !== "psychologist") { toast.error(t("initiatives.login_to_join")); return; }
    if (!user.psychologist_id) { rememberAndGo("/unirme"); return; }
    if (professionMismatch) { toast.error(t("opportunities.profession_mismatch_desc")); return; }
    if (deadlinePassed) { toast.error(t("opportunities.closed_note")); return; }
    setOpen(true);
  };

  const submitParticipation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/initiatives/${id}/join`, form);
      toast.success(t("initiatives.request_sent"));
      localStorage.removeItem("auna_intended_initiative");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const addToCalendar = () => downloadInitiativeIcs(i, { t, lang, publicUrl });

  const ReqRow = ({ met, label }) => (
    <li className="flex items-center gap-2" data-testid={met ? "req-met" : "req-not-met"}>
      {met ? <CheckCircle2 className="h-4 w-4 shrink-0 text-[#1E6B47]" /> : <XCircle className="h-4 w-4 shrink-0 text-red-500" />}
      <span className={met ? "text-slateink/80" : "text-slateink/60"}>{label}</span>
    </li>
  );

  const Stat = ({ label, value, testId }) => (
    <div className="rounded-xl border border-border bg-white p-4 text-center" data-testid={testId}>
      <p className="font-display text-2xl font-bold text-slateink">{value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{label}</p>
    </div>
  );

  const calendarBtn = (testId) => (
    <Button onClick={addToCalendar} variant="outline" className="rounded-full" data-testid={testId}>
      <CalendarPlus className="mr-2 h-4 w-4" /> {t("initiatives.add_to_calendar")}
    </Button>
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <Link to="/iniciativas" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-terracotta" data-testid="back-to-initiatives">
        <ArrowLeft className="h-4 w-4" /> {t("common.back")}
      </Link>

      {showReminderBanner && (
        <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-terracotta/40 bg-terracotta-light/60 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="return-reminder">
          <p className="text-sm text-slateink/90">{t("initiatives.reminder_text")}</p>
          <Button onClick={handleParticipateClick} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="reminder-participate-btn">
            <ClipboardList className="mr-2 h-4 w-4" /> {applyLabel}
          </Button>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-white p-6 sm:p-8" data-testid="initiative-header">
            <div className="flex items-start justify-between gap-3">
              <h1 className="font-display text-3xl font-bold tracking-tight text-slateink break-words">{i.name}</h1>
              <StatusBadge status={i.status} label={t(`status.${i.status}`)} />
            </div>
            <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
              {i.organizer_name && <span>{t("initiatives.organizer")}: <strong className="font-medium text-slateink/80">{i.organizer_name}</strong></span>}
              {i.location && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{i.location}</span>}
              {datesDisplay && <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" />{datesDisplay}</span>}
            </div>
            {i.description && <p className="mt-6 whitespace-pre-line text-sm leading-relaxed text-slateink/80">{i.description}</p>}
          </div>

          {/* Prominent participation CTA + share */}
          <div className="rounded-2xl border border-terracotta/30 bg-terracotta-light/40 p-6 sm:p-8" data-testid="participate-cta">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-xl font-semibold tracking-tight text-slateink">{t("initiatives.how_to_join")}</h2>
              {shareable && <ShareInitiative url={publicUrl} title={i.name} initiativeId={id} />}
            </div>

            {i.status === "closed" ? (
              <p className="mt-3 text-sm text-slateink/80" data-testid="participation-closed">{t("initiatives.participation_closed")}</p>
            ) : i.status !== "published" ? (
              <p className="mt-3 text-sm text-slateink/80" data-testid="participation-not-open">{t("initiatives.not_published")}</p>
            ) : myStatus === "confirmed" ? (
              <div className="mt-3" data-testid="my-status-confirmed">
                <p className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1E6B47]"><CheckCircle2 className="h-4 w-4" />{t("initiatives.request_confirmed")}</p>
                {hasValidDates && <div className="mt-4">{calendarBtn("add-to-calendar-btn")}</div>}
              </div>
            ) : myStatus === "pending" ? (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-slateink/80" data-testid="my-status-pending"><Clock className="h-4 w-4" />{t("initiatives.request_pending")}</p>
            ) : myStatus === "declined" ? (
              <p className="mt-3 text-sm text-slateink/80" data-testid="my-status-declined">{t("initiatives.request_declined")}</p>
            ) : isFull ? (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-slateink/80" data-testid="participation-full"><Info className="h-4 w-4" />{t("initiatives.full_note")}</p>
            ) : deadlinePassed ? (
              <p className="mt-3 inline-flex items-center gap-1.5 text-sm text-slateink/80" data-testid="participation-deadline-passed"><Info className="h-4 w-4" />{t("opportunities.closed_note")}</p>
            ) : !user ? (
              <>
                <p className="mt-3 text-sm text-slateink/80">{t("initiatives.cta_guest_note")}</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Button onClick={() => rememberAndGo("/login")} className="rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="cta-login-btn">
                    <LogIn className="mr-2 h-4 w-4" /> {t("initiatives.cta_login")}
                  </Button>
                  <Button variant="outline" onClick={() => rememberAndGo("/register?role=professional")} className="rounded-full" data-testid="cta-create-profile-btn">
                    <UserPlus className="mr-2 h-4 w-4" /> {t("initiatives.cta_create_profile")}
                  </Button>
                </div>
              </>
            ) : user.role !== "psychologist" ? (
              <p className="mt-3 text-sm text-slateink/80">{t("initiatives.login_to_join")}</p>
            ) : !user.psychologist_id ? (
              <>
                <p className="mt-3 text-sm text-slateink/80">{t("initiatives.need_profile")}</p>
                <Button onClick={() => rememberAndGo("/unirme")} className="mt-4 rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="cta-create-profile-btn">
                  <UserPlus className="mr-2 h-4 w-4" /> {t("initiatives.cta_create_profile")}
                </Button>
              </>
            ) : (
              <>
                <p className="mt-3 text-sm text-slateink/80">{isOpp ? t("opportunities.apply_intro") : t("initiatives.join_intro")}</p>
                <Button onClick={handleParticipateClick} disabled={professionMismatch} size="lg" className="mt-4 rounded-full bg-terracotta hover:bg-terracotta-hover disabled:bg-secondary disabled:text-slateink/50 disabled:opacity-100" data-testid="participate-btn-top">
                  <ClipboardList className="mr-2 h-4 w-4" /> {applyLabel}
                </Button>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label={t("initiatives.goal")} value={goal || "—"} testId="stat-goal" />
            <Stat label={t("initiatives.applicants")} value={i.applicants} testId="stat-applicants" />
            <Stat label={t("initiatives.confirmed")} value={i.confirmed} testId="stat-confirmed" />
            <Stat label={t("initiatives.remaining")} value={goal > 0 ? i.remaining : "—"} testId="stat-remaining" />
          </div>

          <div className="rounded-2xl border border-border bg-white p-6 sm:p-8 space-y-5">
            {modalityLabel && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("initiatives.format_label_display")}</p>
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slateink/80" data-testid="initiative-modality">
                  <Wifi className="h-4 w-4" />{modalityLabel}
                </p>
              </div>
            )}
            {i.languages_needed?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("initiatives.languages_needed")}</p>
                <p className="mt-2 inline-flex items-center gap-1.5 text-sm text-slateink/80"><Globe className="h-4 w-4" />{i.languages_needed.map((l) => languageLabel(l, lang)).join(" · ")}</p>
              </div>
            )}
            {i.is_opportunity && (
              <div data-testid="opportunity-requirements" className="rounded-xl border border-terracotta/30 bg-cream p-4">
                <p className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-terracotta"><Building2 className="h-3.5 w-3.5" />{t("opportunities.section_requirements")}</p>
                <ul className="mt-3 space-y-1.5 text-sm text-slateink/80">
                  {i.required_profession_group && <li><span className="text-muted-foreground">{t("initiatives.category")}:</span> {professionCategoryLabel(i.required_profession_group, lang)}</li>}
                  {i.required_profession && <li><span className="text-muted-foreground">{t("opportunities.required_profession")}:</span> {localize(PROFESSIONS, i.required_profession, lang)}</li>}
                  {i.required_specialty && <li><span className="text-muted-foreground">{t("initiatives.specialty")}:</span> {specialtyLabel(i.required_specialty, lang)}</li>}
                  {i.required_languages?.length > 0 && <li><span className="text-muted-foreground">{t("opportunities.required_languages")}:</span> {i.required_languages.map((l) => languageLabel(l, lang)).join(" · ")}</li>}
                  {i.engagement_type && <li><span className="text-muted-foreground">{t("opportunities.engagement_type")}:</span> {localize(ENGAGEMENT_TYPES, i.engagement_type, lang)}</li>}
                  {i.compensation_type && <li><span className="text-muted-foreground">{t("opportunities.compensation_type")}:</span> {localize(COMPENSATION_TYPES, i.compensation_type, lang)}</li>}
                  {i.min_experience != null && <li><span className="text-muted-foreground">{t("opportunities.min_experience")}:</span> {i.min_experience} {t("opportunities.years")}</li>}
                  {i.professionals_needed != null && <li><span className="text-muted-foreground">{t("opportunities.professionals_needed")}:</span> {i.professionals_needed}</li>}
                  {i.application_deadline && <li><span className="text-muted-foreground">{t("opportunities.application_deadline")}:</span> {formatDateRange(i.application_deadline, null, lang)}</li>}
                  {i.required_credential_status === "verified" && <li>{t("opportunities.verified_required")}</li>}
                </ul>
                {!hasStructuredReq && (
                  <p className="mt-2 text-sm text-muted-foreground">{t("opportunities.no_specific")}</p>
                )}

                {myProfile && hasStructuredReq && (
                  <div className="mt-4 border-t border-terracotta/20 pt-3" data-testid="requirement-comparison">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("opportunities.your_profile")}</p>
                    <ul className="mt-2 space-y-1.5 text-sm">
                      {i.required_profession_group && <ReqRow met={(myProfile.profession_group || "") === i.required_profession_group} label={`${t("initiatives.category")}: ${professionCategoryLabel(i.required_profession_group, lang)}`} />}
                      {i.required_profession && <ReqRow met={myProfile.profession === i.required_profession} label={`${t("opportunities.required_profession")}: ${localize(PROFESSIONS, i.required_profession, lang)}`} />}
                      {i.required_specialty && <ReqRow met={(myProfile.specialties || []).includes(i.required_specialty)} label={`${t("initiatives.specialty")}: ${specialtyLabel(i.required_specialty, lang)}`} />}
                      {i.required_credential_status === "verified" && <ReqRow met={myProfile.verification_status === "verified"} label={t("opportunities.verified_required")} />}
                      {i.min_experience != null && <ReqRow met={(myProfile.years_experience || 0) >= i.min_experience} label={`${i.min_experience}+ ${t("opportunities.years")}`} />}
                      {i.required_languages?.length > 0 && <ReqRow met={i.required_languages.every((l) => (myProfile.languages || []).some((m) => canonicalLang(m) === canonicalLang(l)))} label={`${t("opportunities.required_languages")}: ${i.required_languages.map((l) => languageLabel(l, lang)).join(" · ")}`} />}
                      {i.engagement_type && <ReqRow met={(myProfile.engagement_types || []).includes(i.engagement_type)} label={localize(ENGAGEMENT_TYPES, i.engagement_type, lang)} />}
                    </ul>
                    <p className="mt-2 text-xs text-muted-foreground">{t("opportunities.compare_note")}</p>
                  </div>
                )}

                {professionMismatch && (
                  <div className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3" data-testid="profession-mismatch">
                    <p className="text-sm font-semibold text-red-700">{t("opportunities.profession_mismatch_title")}</p>
                    <p className="mt-1 text-sm text-red-600">{t("opportunities.profession_mismatch_desc")}</p>
                  </div>
                )}
              </div>
            )}
            {i.professional_requirements && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("initiatives.requirements")}</p>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slateink/80">{i.professional_requirements}</p>
              </div>
            )}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-2xl border border-border bg-white p-6">
            {(i.status === "published" || i.status === "closed") && (
              <p className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slateink" data-testid="social-proof">
                <Users className="h-4 w-4 text-terracotta" />{joinedText(i.confirmed)}
              </p>
            )}
            {goal > 0 && (
              <>
                <div className="flex items-center justify-between text-sm text-slateink/80">
                  <span>{i.confirmed} / {goal}</span><span>{pct}%</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-secondary">
                  <div className="h-full rounded-full bg-terracotta" style={{ width: `${pct}%` }} />
                </div>
                {isFull ? (
                  <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-slateink/70" data-testid="full-badge">
                    <Info className="h-3.5 w-3.5" /> {t("initiatives.full_badge")}
                  </p>
                ) : remaining > 0 && remaining <= 3 ? (
                  <p className="mt-3 text-xs font-medium text-terracotta" data-testid="spots-remaining">{spotsText(remaining)}</p>
                ) : null}
              </>
            )}

            {i.status === "published" && myStatus === "confirmed" && hasValidDates ? (
              <div className="mt-5">{calendarBtn("add-to-calendar-btn-side")}</div>
            ) : i.status === "published" && !myStatus && !isFull && !deadlinePassed && user?.psychologist_id ? (
              <Button onClick={handleParticipateClick} disabled={professionMismatch} size="lg" className="mt-5 w-full rounded-full bg-terracotta hover:bg-terracotta-hover disabled:bg-secondary disabled:text-slateink/50 disabled:opacity-100" data-testid="participate-btn">
                <ClipboardList className="mr-2 h-4 w-4" /> {applyLabel}
              </Button>
            ) : (
              <p className="mt-5 text-center text-sm text-muted-foreground" data-testid="sidebar-status">
                {isFull && !myStatus ? t("initiatives.full_badge")
                  : deadlinePassed && !myStatus ? t("opportunities.closed_note")
                  : myStatus === "pending" ? t("initiatives.request_pending")
                  : myStatus === "declined" ? t("initiatives.request_declined")
                  : t(`status.${i.status}`)}
              </p>
            )}
          </div>
        </aside>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="participation-dialog">
          <DialogHeader>
            <DialogTitle>{applyLabel}</DialogTitle>
            <DialogDescription>{isOpp ? t("opportunities.apply_intro") : t("initiatives.participate_desc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitParticipation} className="space-y-4">
            <div><Label>{t("initiatives.p_availability")}</Label><Textarea rows={2} value={form.availability} onChange={(e) => setForm((f) => ({ ...f, availability: e.target.value }))} data-testid="participation-availability" /></div>
            <div><Label>{t("initiatives.p_dates")}</Label><Input value={form.dates} onChange={(e) => setForm((f) => ({ ...f, dates: e.target.value }))} data-testid="participation-dates" /></div>
            <div><Label>{t("initiatives.p_format")}</Label><Input value={form.format_availability} onChange={(e) => setForm((f) => ({ ...f, format_availability: e.target.value }))} data-testid="participation-format" /></div>
            <div><Label>{t("initiatives.p_notes")}</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="participation-notes" /></div>
            <Button type="submit" disabled={submitting} className="w-full rounded-full bg-terracotta hover:bg-terracotta-hover" data-testid="participation-submit">
              {submitting ? t("common.saving") : t("common.submit")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
